import React, { useState } from 'lib/react';
import {
  EVButton,
  EVGrid, EVPaper, EVTypography, makeStyles, EVSkeleton,
  EVKeyboardArrowRight, EVKeyboardArrowUp, EVKeyboardArrowDown,
} from 'lib/components';
import { useLocation, Link } from 'lib/router';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';
import clsx from 'lib/clsx';
import { REPORT_DETAIL_ROUTE } from 'constants/RouteConstants';
import Typography from 'commonComponents/typography';
import { hasExpandableContent } from 'utils/app.utils';

const useStyles = makeStyles((theme) => ({
  reportsList: {
    backgroundColor: theme.evPalette.charcoal[100],
    padding: '8px',
    rowGap: '8px',
  },
  reportListItemPaper: {
    position: 'relative',
    boxShadow: 'none',
    '&.border': {
      border: `1px solid ${theme.evPalette.ocean[600]}`,
    },
  },
  reportListItem: {
    position: 'relative',
    padding: '16px',
    rowGap: '8px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    textDecoration: 'none',
    color: 'inherit',
    '&>div': {
      '&:first-child': { fontWeight: 600 },
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '18px',
      color: theme.evPalette.charcoal[900],
    },
  },
  reportDetailRow: {
    columnGap: '16px',
    '&>div>p': {
      color: theme.evPalette.charcoal[800],
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '24px',
      '&:first-child': {
        textTransform: 'uppercase',
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: '18px',
        color: theme.evPalette.charcoal[900],
      },
    },
  },
  actions: {
    gap: '8px',
    '&>*': {
      width: 'unset',
      flex: '1 0 40%',
      justifyContent: 'center',
    },
  },
  detailsBtn: {
    position: 'absolute',
    right: 0,
    top: 0,
    boxSizing: 'border-box',
    height: '100%',
    minWidth: '44px',
    width: '44px',
    borderRight: '5px solid',
    borderRadius: '0px',
    padding: 0,
  },
  expandBtn: {
    padding: '10px 12px',
    cursor: 'pointer',
  },
  expandBtnLabel: {
    display: 'flex',
    gap: '8px',
  },
  expandedRow: {
    display: 'flex',
    width: 'fit-content',
    gap: '24px',
    paddingLeft: '8px',
    flexWrap: 'wrap',
    padding: '0px 16px',
    transition: 'padding 0.5s ease',
    '&.expanded': {
      padding: '16px',
    },
  },
}));

/** Component to render the Report details in list format for Order History Page in mobile view */
function ReportsList({
  reportDetailsList, ExpandedRowComponent, expandedRowFlags, onExpandedRowFlagsChange, loading, fields,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const classes = useStyles();

  const currentUrl = `${location.pathname}${location.search}`;

  const displayHeader = (title) => (loading ? <EVSkeleton style={{ borderRadius: '6px' }} width={63} height={18} animation="wave" /> : title);

  const fieldNames = fields.map(({ dataKey }) => dataKey);

  const showField = (field) => fieldNames.includes(field);

  const [expandedRowFlagsState, setExpandedRowFlags] = useState({});

  const handleExpandedRowVisibility = (key) => {
    if (onExpandedRowFlagsChange) return onExpandedRowFlagsChange(key);
    return setExpandedRowFlags((prevState) => ({ ...prevState, [key]: !prevState[key] }));
  };
  const expandedRows = expandedRowFlags || expandedRowFlagsState;

  return (
    <EVGrid className={classes.reportsList} container direction="column">
      {(reportDetailsList?.length > 0) ? reportDetailsList.map(({
        rowKey,
        product, status,
        address,
        reportId, date, delivery, cost,
        reportIdCell,
        actions, expandedRow,
      }) => {
        const [detailsButton, ...restActions] = actions ? actions.props.children : [];
        return (
          <EVPaper
            key={rowKey}
            className={clsx(classes.reportListItemPaper, detailsButton && 'border')}
          >
            <EVGrid
              className={classes.reportListItem}
              component={Link}
              to={detailsButton && {
                pathname: REPORT_DETAIL_ROUTE.replace(':reportId', reportId),
                state: { currentUrl },
              }}
            >
              <EVGrid container justifyContent="space-between" alignItems="center">
                {product}
                {status}
              </EVGrid>
              <EVGrid>
                {address}
              </EVGrid>
              <EVGrid container wrap="nowrap" className={classes.reportDetailRow}>
                <EVGrid>
                  <EVTypography>{displayHeader(t('order_history__report_id'))}</EVTypography>
                  <EVTypography>{reportIdCell}</EVTypography>
                </EVGrid>
                <EVGrid>
                  <EVTypography>{displayHeader(t('order_history__date_ordered'))}</EVTypography>
                  <EVTypography>{date}</EVTypography>
                </EVGrid>
                {showField('delivery') && (
                  <EVGrid>
                    <EVTypography>{displayHeader(t('common_delivery'))}</EVTypography>
                    <EVTypography>{delivery}</EVTypography>
                  </EVGrid>
                )}
                {showField('cost') && (
                  <EVGrid>
                    <EVTypography>{displayHeader(t('common__cost'))}</EVTypography>
                    <EVTypography>{cost}</EVTypography>
                  </EVGrid>
                )}
              </EVGrid>
              <EVGrid container className={classes.actions}>
                {restActions}
              </EVGrid>
              {detailsButton && (
              <EVButton
                id={`detailsBtn-${reportId}`}
                className={classes.detailsBtn}
                color="primary"
              >
                <EVKeyboardArrowRight />
              </EVButton>
              )}
            </EVGrid>
            <EVGrid style={{ display: 'flex', flexDirection: 'column' }}>
              {hasExpandableContent(expandedRow) && ExpandedRowComponent && (
                <EVButton
                  id={`expand-btn-${rowKey}`}
                  className={classes.expandBtn}
                  onClick={() => handleExpandedRowVisibility(rowKey)}
                >
                  <Typography variant="body-500" fontColor="ocean-600" className={classes.expandBtnLabel}>
                    {expandedRows[rowKey] ? <EVKeyboardArrowUp /> : <EVKeyboardArrowDown />}
                    {expandedRows[rowKey] ? 'Less' : 'More'}
                  </Typography>
                </EVButton>
              )}
              <EVGrid className={classes.reportDetailRow}>
                <EVGrid className={clsx(
                  classes.expandedRow,
                  expandedRows[rowKey] && hasExpandableContent(expandedRow) && 'expanded',
                )}
                >
                  {expandedRows[rowKey] && hasExpandableContent(expandedRow) && <ExpandedRowComponent expandedRowData={expandedRow} />}
                </EVGrid>
              </EVGrid>
            </EVGrid>
          </EVPaper>
        );
      }) : <EVGrid>No results</EVGrid>}
    </EVGrid>
  );
}

ReportsList.defaultProps = {
  ExpandedRowComponent: null,
  expandedRowFlags: null,
  onExpandedRowFlagsChange: null,
};
ReportsList.propTypes = {
  reportDetailsList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  loading: PropTypes.bool.isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  ExpandedRowComponent: PropTypes.func,
  expandedRowFlags: PropTypes.arrayOf(PropTypes.bool),
  onExpandedRowFlagsChange: PropTypes.func,
};

export default ReportsList;
